/* eslint-disable indent */
import React, { useState } from "react";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { ArrowUpDown, Beer, CalendarDays, CheckSquare, Coins, Grid2x2, List, Search, Store, Trash2 } from "lucide-react";
import EntityLightbox from "@/components/molecules/EntityLightbox";
import EntityListItem from "@/components/molecules/EntityListItem";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
interface EntityManagementProps {
  entities: any[];
  isLoading: boolean;
  variant?: "shop" | "tavern" | "loot";
  onDelete: (ids: string[]) => void;
  onUpdate: (id: string, data: any) => void;
  EntityView: React.ComponentType<any>;
}
type ViewMode = "grid" | "list";
type SortOption = "date" | "name" | "type" | "size";
type SortDirection = "asc" | "desc";
const sortOptions = [{
  value: "date",
  label: "Date",
  icon: CalendarDays
}, {
  value: "name",
  label: "Name",
  icon: Store
}, {
  value: "type",
  label: "Type",
  icon: Store
}, {
  value: "size",
  label: "Size",
  icon: ArrowUpDown
}] as const;
const EntityManagement: React.FC<EntityManagementProps> = ({
  entities,
  isLoading,
  variant = "shop",
  onDelete,
  onUpdate,
  EntityView
}) => {
  // View state
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [lightboxEntity, setLightboxEntity] = useState<any | null>(null);

  // Search and sort state
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const icon = variant === "shop" ? Store : variant === "tavern" ? Beer : Coins;
  const bgColor = "bg-slate-800/50";
  const borderColor = "border-slate-700";
  const textColor = "text-slate-200";
  const mutedTextColor = "text-slate-400";
  const accentBgColor = "bg-sky-400/10";
  const accentTextColor = "text-sky-400";
  const getEntityName = (entity: any) => {
    switch (variant) {
      case "shop":
        return entity.outputData.shopName;
      case "tavern":
        return entity.outputData.tavernName;
      case "loot":
        return entity.outputData.lootName;
      default:
        return "";
    }
  };
  const filteredAndSortedEntities = entities.filter(entity => getEntityName(entity) != null && getEntityName(entity).toLowerCase().includes(searchTerm.toLowerCase())).sort((a, b) => {
    switch (sortBy) {
      case "date":
        return sortDirection === "desc" ? new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime() : new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      case "name":
        const aName = getEntityName(a);
        const bName = getEntityName(b);
        return sortDirection === "desc" ? bName.localeCompare(aName) : aName.localeCompare(bName);
      default:
        return 0;
    }
  });
  const handleViewModeToggle = () => {
    setViewMode(prev => prev === "grid" ? "list" : "grid");
  };
  const handleSelect = (id: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };
  const handleSelectAll = () => {
    if (selectedIds.size === filteredAndSortedEntities.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredAndSortedEntities.map(entity => entity.id)));
    }
  };
  const handleSort = (option: SortOption) => {
    if (sortBy === option) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortBy(option);
      setSortDirection("desc");
    }
  };
  const handleDelete = async () => {
    await onDelete(Array.from(selectedIds));
    setSelectedIds(new Set());
    setIsDeleteDialogOpen(false);
  };
  const handleUpdate = async (id: string, data: any) => {
    try {
      // Call the parent's onUpdate (which makes the API call)
      await onUpdate(id, data);
      // Update the lightbox entity if it's open
      if (lightboxEntity && lightboxEntity.id === id) {
        setLightboxEntity({
          ...lightboxEntity,
          outputData: data
        });
      }
      setLightboxEntity(null);
    } catch (error) {
      toast.error(`Failed to update ${variant}. Please try again.`);
    }
  };
  const handleOpenLightbox = (entity: any) => {
    setLightboxEntity(entity);
  };
  const handleCloseLightbox = () => {
    setLightboxEntity(null);
  };
  return <div className="space-y-6" data-sentry-component="EntityManagement" data-sentry-source-file="index.tsx">
            {/* Controls */}
            <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="flex gap-2">
                    <Button variant="ghost" size="icon" onClick={handleViewModeToggle} className={cn(viewMode === "grid" ? accentBgColor + " " + accentTextColor : "hover:bg-sky-400/5 " + mutedTextColor)} title="Toggle view mode" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        {viewMode === "grid" ? <Grid2x2 className="w-5 h-5" /> : <List className="w-5 h-5" />}
                    </Button>
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-sentry-element="Search" data-sentry-source-file="index.tsx" />
                        <Input type="text" placeholder={`Search ${variant}s...`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className={cn("pl-9 bg-black/20", borderColor, textColor)} data-sentry-element="Input" data-sentry-source-file="index.tsx" />
                    </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <DropdownMenu data-sentry-element="DropdownMenu" data-sentry-source-file="index.tsx">
                        <DropdownMenuTrigger asChild data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="index.tsx">
                            <Button variant="ghost" size="sm" className="flex items-center gap-2" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                                <ArrowUpDown className="w-4 h-4" data-sentry-element="ArrowUpDown" data-sentry-source-file="index.tsx" />
                                <span>Sort by {sortBy}</span>
                                {sortDirection === "asc" ? " (A→Z)" : " (Z→A)"}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" data-sentry-element="DropdownMenuContent" data-sentry-source-file="index.tsx">
                            {sortOptions.map(({
              value,
              label,
              icon: Icon
            }) => <DropdownMenuItem key={value} onClick={() => handleSort(value)} className={cn("flex items-center gap-2", sortBy === value && accentBgColor + " " + accentTextColor)}>
                                    <Icon className="w-4 h-4" />
                                    <span>{label}</span>
                                </DropdownMenuItem>)}
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <Button variant={isSelectionMode ? "secondary" : "ghost"} size="sm" onClick={() => {
          setIsSelectionMode(!isSelectionMode);
          if (!isSelectionMode) {
            setSelectedIds(new Set());
          }
        }} className={cn("flex items-center gap-2", isSelectionMode && accentBgColor + " " + accentTextColor)} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        <CheckSquare className="w-4 h-4" data-sentry-element="CheckSquare" data-sentry-source-file="index.tsx" />
                        <span className="hidden sm:inline">
                            {isSelectionMode ? "Exit Selection" : "Select Items"}
                        </span>
                    </Button>
                    {isSelectionMode && filteredAndSortedEntities.length > 0 && <Button variant="ghost" size="sm" onClick={handleSelectAll} className={cn("flex items-center gap-2", selectedIds.size === filteredAndSortedEntities.length && accentBgColor + " " + accentTextColor)}>
                                <Store className="w-4 h-4" />
                                <span className="hidden sm:inline">
                                    {selectedIds.size === filteredAndSortedEntities.length ? "Deselect All" : "Select All"}
                                </span>
                            </Button>}
                    {selectedIds.size > 0 && <Button variant="ghost" size="sm" onClick={() => setIsDeleteDialogOpen(true)} className="flex items-center gap-2 text-red-400 hover:bg-red-400/10">
                            <Trash2 className="w-4 h-4" />
                            <span className="hidden sm:inline">
                                Delete {selectedIds.size}{" "}
                                {selectedIds.size === 1 ? variant : variant + "s"}
                            </span>
                        </Button>}
                </div>
            </div>

            {/* Entity List/Grid */}
            <div className="space-y-4">
                {isLoading ? <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className={cn(viewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-4")}>
                        {Array.from({
          length: 6
        }).map((_, i) => <div key={i} className={cn("rounded-lg p-6 animate-pulse", bgColor)}>
                                <div className="h-8 bg-slate-700 rounded w-3/4 mb-4"></div>
                                <div className="space-y-2">
                                    <div className="h-4 bg-slate-700 rounded w-1/2"></div>
                                    <div className="h-4 bg-slate-700 rounded w-full"></div>
                                    <div className="h-4 bg-slate-700 rounded w-2/3"></div>
                                </div>
                            </div>)}
                    </motion.div> : filteredAndSortedEntities.length === 0 ? <div className={cn("text-center py-8", mutedTextColor)}>
                        No{" "}
                        {variant === "shop" ? "shops" : variant === "tavern" ? "taverns" : "loot collections"}{" "}
                        found.
                    </div> : <motion.div layout className={cn(viewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-4")}>
                        {filteredAndSortedEntities.map(entity => <EntityListItem key={entity.id} entity={entity} variant={variant} icon={icon} isSelected={selectedIds.has(entity.id)} onSelect={() => handleSelect(entity.id)} onClick={() => handleOpenLightbox(entity)} isSelectionMode={isSelectionMode} />)}
                    </motion.div>}
            </div>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen} data-sentry-element="AlertDialog" data-sentry-source-file="index.tsx">
                <AlertDialogContent data-sentry-element="AlertDialogContent" data-sentry-source-file="index.tsx">
                    <AlertDialogHeader data-sentry-element="AlertDialogHeader" data-sentry-source-file="index.tsx">
                        <AlertDialogTitle data-sentry-element="AlertDialogTitle" data-sentry-source-file="index.tsx">Confirm Deletion</AlertDialogTitle>
                        <AlertDialogDescription data-sentry-element="AlertDialogDescription" data-sentry-source-file="index.tsx">
                            Are you sure you want to delete {selectedIds.size}{" "}
                            {selectedIds.size === 1 ? variant : variant + "s"}?
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter data-sentry-element="AlertDialogFooter" data-sentry-source-file="index.tsx">
                        <AlertDialogCancel data-sentry-element="AlertDialogCancel" data-sentry-source-file="index.tsx">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600" data-sentry-element="AlertDialogAction" data-sentry-source-file="index.tsx">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Lightbox */}
            {lightboxEntity && <EntityLightbox title={getEntityName(lightboxEntity)} variant={variant} onClose={handleCloseLightbox}>
                    <EntityView {...variant === "shop" ? {
        shop: lightboxEntity
      } : variant === "tavern" ? {
        tavern: lightboxEntity
      } : {
        loot: lightboxEntity
      }} onEdit={handleUpdate} />
                </EntityLightbox>}
        </div>;
};
export default EntityManagement;